import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Check, ChevronRight, Sparkles, Lock, Trophy, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useLaunchPath, type LaunchPathItem } from "@/hooks/useLaunchPath";
import { trackMetaEvent } from "@/lib/metaPixel";
import {
  fireFirstLessonStarted,
  fireFirstSimulationStarted,
  fireFirstLessonCompleted,
  fireFirstSimulationCompleted,
} from "@/lib/firstSession";
import { launchItemRoute } from "@/lib/launchPath";
import RetakeLessonDialog from "@/components/lesson/RetakeLessonDialog";
import RetakeModuleDialog from "@/components/modules/RetakeModuleDialog";
import { useLessonRetake } from "@/hooks/useLessonRetake";
import { useModuleRetake } from "@/hooks/useModuleRetake";
import { useLearningEngine } from "@/hooks/useLearningEngine";
import { toast } from "sonner";

/**
 * Launch Path activation card.
 * Renders only when an admin has curated at least one active launch_path_items row.
 * Hides itself when the learner has completed every item, so the dashboard stays
 * minimalist (Command Center philosophy).
 */
export default function LaunchPathCard() {
  const navigate = useNavigate();
  const { items, completedCount, totalCount, nextItem, done, loading, refetch } = useLaunchPath();
  const { startRetake, pending: lessonRetakePending } = useLessonRetake();
  const { reverseAndReset, pending: moduleRetakePending } = useModuleRetake();
  // Engine authority: once the Adaptive Learning Engine returns a real
  // decision, it is the source of truth for what the learner should do
  // next. The Launch Path list below stays visual (progress overview
  // + retake affordances), but its primary CTA defers to the engine.
  const { nextAction: engineDecision } = useLearningEngine();
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const prevCompletedRef = useRef<number | null>(null);
  const [lessonToRetake, setLessonToRetake] = useState<LaunchPathItem | null>(null);
  const [moduleToRetake, setModuleToRetake] = useState<LaunchPathItem | null>(null);

  // Fire once per session when the learner first sees an actionable launch path.
  useEffect(() => {
    if (loading || totalCount === 0 || startedRef.current) return;
    if (sessionStorage.getItem("hw_launch_path_started")) {
      startedRef.current = true;
      return;
    }
    startedRef.current = true;
    sessionStorage.setItem("hw_launch_path_started", "1");
    void trackMetaEvent("launch_path_started", {
      customData: { total: totalCount, completed: completedCount },
    });
  }, [loading, totalCount, completedCount]);

  // Fire when the launch path flips to fully done.
  useEffect(() => {
    if (!done || completedRef.current) return;
    completedRef.current = true;
    void trackMetaEvent("launch_path_completed", { customData: { total: totalCount } });
  }, [done, totalCount]);

  // Detect the FIRST completion in this Launch Path and emit first-session
  // events based on whichever item just flipped to completed.
  useEffect(() => {
    if (loading) return;
    const prev = prevCompletedRef.current;
    prevCompletedRef.current = completedCount;
    if (prev === null) return; // first observation, no transition yet
    if (completedCount <= prev) return;
    // Find the most recently completed item by scanning in order.
    const justCompleted = items
      .filter((i) => i.completed)
      .slice(prev, completedCount);
    for (const it of justCompleted) {
      if (it.itemType === "lesson") {
        fireFirstLessonCompleted({ source: "launch_path" });
      } else {
        fireFirstSimulationCompleted(it.refId);
      }
    }
  }, [loading, completedCount, items]);

  if (loading || totalCount === 0) return null;

  const pct = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

  const goTo = (item: typeof items[number]) => {
    if (item.locked) return;
    // Safe-lock guard: if the engine has resolved a next-action, do not
    // let a stale list-item click bypass it. The engine wins unless it
    // would create an unsafe unlock — in which case we hard-block.
    if (engineDecision) {
      const engineTarget = engineDecision.target_lesson_id;
      const engineLocked = engineDecision.is_locked || engineDecision.action === "error_safe_locked";
      if (engineLocked) {
        toast.info("Progress review needed", {
          description: engineDecision.lock_reason ?? "The learning engine is holding progression while it verifies your last step.",
        });
        return;
      }
      // Only enforce lesson-target mismatches. Module-level items and
      // items without engine-side lesson targets pass through so the
      // learner can freely review completed items.
      if (
        !item.completed &&
        item.itemType === "lesson" &&
        engineTarget &&
        engineTarget !== item.refId
      ) {
        toast.info("Follow your next best action", {
          description: "Your Adaptive Learning Engine has a different next step queued for you. Use the card above to continue.",
        });
        return;
      }
    }
    if (item.itemType === "lesson") {
      fireFirstLessonStarted({ source: "launch_path" });
    } else {
      fireFirstSimulationStarted(item.refId);
    }
    navigate(launchItemRoute(item.itemType, item.refId));
  };

  const confirmLessonRetake = async () => {
    if (!lessonToRetake) return;
    await startRetake(lessonToRetake.refId);
    const route = launchItemRoute(lessonToRetake.itemType, lessonToRetake.refId);
    setLessonToRetake(null);
    navigate(route);
  };

  const confirmModuleRetake = async () => {
    if (!moduleToRetake) return;
    const result = await reverseAndReset(moduleToRetake.refId);
    if (!result) return;
    const route = launchItemRoute(moduleToRetake.itemType, moduleToRetake.refId);
    setModuleToRetake(null);
    await refetch();
    navigate(route);
  };

  return (
    <section
      aria-labelledby="launch-path-heading"
      className="rounded-3xl border border-primary/20 bg-card/95 backdrop-blur-xl p-6 sm:p-8 shadow-[0_20px_60px_-30px_hsl(var(--primary)/0.4)]"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary/10 p-2.5 text-primary">
            <Rocket className="h-5 w-5" />
          </div>
          <div>
            <h2
              id="launch-path-heading"
              className="font-heading text-lg sm:text-xl font-bold text-foreground flex items-center gap-2"
            >
              {done ? "Launch Path Complete" : "Your Launch Path"}
              {done ? <Trophy className="h-4 w-4 text-emerald-500" aria-hidden /> : <Sparkles className="h-4 w-4 text-primary" aria-hidden />}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {done ? "Review any completed step or intentionally retake lessons and modules." : "A curated first-session journey through HeirWise."}
            </p>
          </div>
        </div>
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
          {completedCount} / {totalCount}
        </span>
      </div>

      <Progress value={pct} className="mb-5 h-2" />

      <ol className="space-y-2">
        {items.map((item, idx) => {
          const isNext = nextItem?.id === item.id;
          return (
            <li key={item.id} className="space-y-2">
              <button
                type="button"
                onClick={() => goTo(item)}
                disabled={item.locked}
                aria-disabled={item.locked}
                className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition ${
                  item.completed
                    ? "border-emerald-500/30 bg-emerald-500/5 text-foreground/80"
                    : item.locked
                      ? "border-border/40 bg-background/20 text-muted-foreground cursor-not-allowed"
                      : isNext
                        ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
                        : "border-border/60 bg-background/40 hover:bg-background/60"
                }`}
              >
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                    item.completed
                      ? "bg-emerald-500/20 text-emerald-500"
                      : item.locked
                        ? "bg-muted/50 text-muted-foreground"
                        : isNext
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                  }`}
                  aria-hidden
                >
                  {item.completed ? (
                    <Check className="h-4 w-4" />
                  ) : item.locked ? (
                    <Lock className="h-3.5 w-3.5" />
                  ) : (
                    idx + 1
                  )}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold truncate">{item.title}</span>
                  {item.subtitle && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {item.subtitle}
                    </span>
                  )}
                </span>
                {isNext && !item.locked && (
                  <span className="text-xs font-medium text-primary flex items-center gap-1">
                    Start <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                )}
                {item.completed && !isNext && !item.locked && (
                  <span className="text-xs font-medium text-foreground/70 flex items-center gap-1">
                    Review <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                )}
                {item.locked && (
                  <span className="text-xs font-medium text-muted-foreground">
                    Locked
                  </span>
                )}
              </button>
              {item.completed && !item.locked && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => item.itemType === "lesson" ? setLessonToRetake(item) : setModuleToRetake(item)}
                    className="h-8 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    {item.itemType === "lesson" ? "Retake Lesson" : "Retake Module"}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Primary CTA is owned by EngineStatusCard. Only fall back to
          the LaunchPathCard's own CTA when the engine has no live
          decision or is in safe-locked review — never render two
          competing "continue" buttons. */}
      {nextItem && !nextItem.locked && (!engineDecision || engineDecision.action === "error_safe_locked") && (() => {
        const engineSafeLocked =
          !!engineDecision &&
          (engineDecision.is_locked || engineDecision.action === "error_safe_locked");
        return (
          <div className="mt-5 space-y-2">
            <Button
              onClick={() => goTo(nextItem)}
              className="w-full sm:w-auto"
              size="lg"
              disabled={engineSafeLocked}
              aria-disabled={engineSafeLocked}
            >
              {engineSafeLocked ? (
                <>
                  <Lock className="mr-1.5 h-4 w-4" />
                  Progress review needed
                </>
              ) : (
                <>
                  Continue Launch Path
                  <ChevronRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
            {engineSafeLocked && (
              <p className="text-xs text-muted-foreground">
                {engineDecision?.lock_reason ??
                  "The learning engine is holding progression while it verifies your last step. Refresh in a moment or contact support if this persists."}
              </p>
            )}
          </div>
        );
      })()}
      {engineDecision && engineDecision.action !== "error_safe_locked" && !done && (
        <p className="mt-5 text-xs text-muted-foreground">
          Your next best action is set by the HeirWise Adaptive Learning Engine card above.
        </p>
      )}
      <RetakeLessonDialog
        open={!!lessonToRetake}
        onOpenChange={(open) => !open && setLessonToRetake(null)}
        onConfirm={confirmLessonRetake}
        pending={lessonRetakePending}
      />
      <RetakeModuleDialog
        open={!!moduleToRetake}
        onOpenChange={(open) => !open && setModuleToRetake(null)}
        moduleDisplayName={moduleToRetake?.title ?? "Module"}
        onConfirm={confirmModuleRetake}
        pending={moduleRetakePending}
      />
    </section>
  );
}